'use client';
import { createContext, type Dispatch, type SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { useUser } from '@/app_lib/providers/UserProvider';
import type { PaywallState } from '@/app_lib/types/Paywall';
import { PianoConfig } from '@/app_lib/types/Piano';
import { isPaywallContent } from '@/app_lib/utils/utils';

interface PaywallProps {
  children: React.ReactNode;
}

interface PaywallContextInterface {
  paywall30: PaywallState;
  paywallState: { paywallCheck: PaywallState };
  showPaywallax: boolean;
  setPaywallState: Dispatch<SetStateAction<{ paywallCheck: PaywallState }>>;
  setPaywall30: Dispatch<SetStateAction<PaywallState>>;
  setShowPaywallax: Dispatch<SetStateAction<boolean>>;
}

const defaultPaywallContext: PaywallContextInterface = {
  paywall30: 'pending',
  paywallState: { paywallCheck: 'pending' },
  setPaywall30: (() => {}) as Dispatch<SetStateAction<PaywallState>>,
  setPaywallState: (() => {}) as Dispatch<SetStateAction<{ paywallCheck: PaywallState }>>,
  setShowPaywallax: (() => {}) as Dispatch<SetStateAction<boolean>>,
  showPaywallax: false,
};

// biome-ignore lint/style/useComponentExportOnlyModules: context should be with provider
export const PaywallContext = createContext<PaywallContextInterface | undefined>(undefined);

export const PaywallProvider = ({ children }: PaywallProps) => {
  const { pianoPageConfig } = useUser();

  const [paywallState, setPaywallState] = useState(defaultPaywallContext.paywallState);
  const [paywall30, setPaywall30] = useState<PaywallState>(defaultPaywallContext.paywall30);
  const [showPaywallax, setShowPaywallax] = useState<boolean>(defaultPaywallContext.showPaywallax);

  // Currently unused hook for observing paywall iframe events
  // Uncomment if paywall event tracking is needed in the future
  // usePaywallIframeObserver();

  useEffect(() => {
    if (!pianoPageConfig) {
      return;
    }
    const paywallContent = isPaywallContent(pianoPageConfig);

    let paywallCheck = paywall30;
    if (!paywallContent || (paywallContent && paywall30 === 'no-paywall')) {
      // Not paywallable content OR paywallable content and not paywalled
      paywallCheck = 'no-paywall';
    } else if (paywallContent && paywall30 === 'paywalled') {
      // Paywallable content and paywalled
      paywallCheck = 'paywalled';
    }

    setPaywallState({ paywallCheck });
  }, [paywall30, pianoPageConfig]);

  useEffect(() => {
    if (pianoPageConfig && pianoPageConfig.contentSection === PianoConfig.BypassSection && paywall30 === 'paywalled') {
      const paywallAnchor = document.getElementsByClassName('paywall-selector')[0];
      paywallAnchor?.remove();
    }
  }, [paywall30, pianoPageConfig]);

  const memoUserPaywallvalue = useMemo(
    () => ({
      paywall30,
      paywallState,
      setPaywall30,
      setPaywallState,
      setShowPaywallax,
      showPaywallax,
    }),
    [paywallState, paywall30, showPaywallax],
  );

  return <PaywallContext.Provider value={memoUserPaywallvalue}>{children}</PaywallContext.Provider>;
};

// biome-ignore lint/style/useComponentExportOnlyModules: context should be with provider
export const usePaywall = () => {
  const context = useContext(PaywallContext);
  if (!context) {
    throw new Error('usePaywall must be used within a PaywallProvider');
  }
  return context;
};
